import React, { useMemo } from 'react';
import { ButtonItem } from '@atlaskit/menu';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { useIntl } from '@atlassian/jira-intl';
import type { EntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { MenuIcon } from '@atlassian/automation-manual-triggers';
import RecurWorkIcon from '@atlassian/jira-icons/src/ui/recur-work-icon/index.tsx';
import { useRecurIssueProperties } from '@atlassian/jira-recur-work-item/src/controllers/selectors/index.tsx';
import { parse } from '@atlassian/jira-ical/src/parse.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { ParseResult } from '@atlassian/jira-ical/src/types.tsx';
import { getRecurMenuItemLabel } from '@atlassian/jira-recur-work-item/src/ui/utils.tsx';

type Props = {
	buttonRef: EntryPointButtonTrigger;
	onClick: () => void;
};

export const RecurWorkMenuItem = ({ buttonRef, onClick }: Props) => {
	const { formatMessage } = useIntl();

	const [issueProperties] = useRecurIssueProperties();

	const menuItemMessage = useMemo(() => {
		const isRecurOnComplete =
			!!issueProperties &&
			(issueProperties.templateId === undefined || issueProperties.templateId === null);

		let parsedrRule: ParseResult | undefined;
		try {
			parsedrRule = parse(issueProperties?.recurOnScheduleConfig?.rRule);
		} catch (err) {
			log.safeErrorWithoutCustomerData(
				'jira-issue-field-status.src.ui.status-view.actions.actions-menu.recur-work-menu-item',
				'Error parsing rRule string',
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				err as Error,
			);
		}
		const { frequency, interval, byDay } = parsedrRule?.event?.rrule ?? {};

		return getRecurMenuItemLabel({ frequency, interval, byDay }, isRecurOnComplete);
	}, [issueProperties]);

	return (
		<ButtonItem
			ref={buttonRef}
			onClick={onClick}
			iconBefore={
				<MenuIcon>
					<RecurWorkIcon label="" />
				</MenuIcon>
			}
			iconAfter={<ChevronRightIcon label="" />}
			testId="issue-field-status.ui.status-view.actions.actions-menu.recur-work-menu-item.button-item"
		>
			{formatMessage(menuItemMessage)}
		</ButtonItem>
	);
};
